import { useMemo } from 'react';
import { cn } from '@/lib/cn';
import { useSession } from '@/hooks/useSession';
import { useMediaStorage } from '@/components/MediaStorageProvider';
import { StorageUsageIndicator } from '@/components/StorageUsageIndicator';
import { isOssStorageMode } from '@/lib/mediaStorage';

type Props = {
  onChangePassword: () => void;
  className?: string;
};

function accountValidityLine(
  role: 'admin' | 'user',
  ymd: string | null,
): { text: string; className: string } {
  if (role === 'admin') {
    return { text: '系统管理员', className: 'text-slate-500' };
  }
  if (!ymd) {
    return { text: '有效期不限', className: 'text-slate-500' };
  }
  const d = ymd.slice(0, 10);
  const t = new Date();
  const todayStr = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  if (d < todayStr) {
    return { text: `已于 ${d} 到期`, className: 'text-red-600 font-medium' };
  }
  if (d === todayStr) {
    return { text: `今日到期（${d}）`, className: 'text-amber-700 font-medium' };
  }
  return { text: `有效期至 ${d}`, className: 'text-slate-500' };
}

/** 桌面顶栏右侧：账号、有效期、资源占用、修改密码 */
export function HeaderAccountBar({ onChangePassword, className }: Props) {
  const { username, role, accountValidUntil } = useSession();
  const { mediaStorage } = useMediaStorage();

  const validity = useMemo(
    () => accountValidityLine(role, accountValidUntil),
    [role, accountValidUntil],
  );

  const showStorage =
    role !== 'admin' &&
    mediaStorage &&
    (mediaStorage.quota_bytes != null || isOssStorageMode(mediaStorage.storage_mode));

  if (!username) {
    return null;
  }

  return (
    <div className={cn('flex flex-wrap items-center justify-end gap-3', className)}>
      {showStorage ? <StorageUsageIndicator status={mediaStorage} variant="header" /> : null}
      <div className="flex items-center gap-3 border-slate-200 pl-0 sm:border-l sm:pl-3">
        <div className="min-w-0 text-right">
          <p className="truncate text-sm font-semibold text-slate-900" title={username}>
            {username}
          </p>
          <p className={cn('text-xs leading-snug', validity.className)}>{validity.text}</p>
        </div>
        <button
          type="button"
          className="shrink-0 rounded-lg px-2.5 py-1.5 text-xs font-medium text-teal-700 ring-1 ring-teal-200/80 transition hover:bg-teal-50 hover:text-teal-800"
          onClick={onChangePassword}
        >
          修改密码
        </button>
      </div>
    </div>
  );
}
